import { useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";

import type { AIContext } from "../../services/aiService";
import { inventoryService, type ItemCategory } from "../../services/inventoryService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import { Modal } from "../Modal";
import { ActionPanel } from "../register/DetailsModal";

export const errorLine = (error: unknown, fallback: string) => (
  <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(error, fallback)}</p>
);

/** An action that needs a reason on the record, with the writing assistant beside it. */
export function ReasonPanel({ title, description, label = "Why", placeholder, context, button, danger, busy, error, errorText, onSubmit, children }: {
  title: string;
  description?: string;
  label?: string;
  placeholder: string;
  context: AIContext;
  button: string;
  danger?: boolean;
  busy: boolean;
  error: unknown;
  errorText: string;
  onSubmit: (reason: string) => void;
  /** Fields that come before the reason, e.g. a new status. */
  children?: ReactNode;
}) {
  const [reason, setReason] = useState("");
  return (
    <ActionPanel title={title} description={description}>
      {children}
      <Field label={label} as="div">
        <AIAssistField
          rows={2}
          value={reason}
          onChange={(next) => setReason(next)}
          placeholder={placeholder}
          context={context}
          draftLabel="Draft it"
          draftHint="Jot the facts first, and it writes them up. It never guesses a reason."
        />
      </Field>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={!reason.trim() || busy}
          onClick={() => onSubmit(reason.trim())}
          className={danger ? "rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50" : "btn btn-primary"}
        >
          {busy ? "Saving…" : button}
        </button>
      </div>
      {Boolean(error) && errorLine(error, errorText)}
    </ActionPanel>
  );
}

/** The categories that group stock and assets alike. */
export function CategoriesModal({ categories, usage, onClose, onChanged }: {
  categories: ItemCategory[];
  /** How many items and assets sit in each category. */
  usage: Map<number, number>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<ItemCategory | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const reset = () => {
    setEditing(null);
    setName("");
    setDescription("");
  };
  const save = useMutation({
    mutationFn: () => (editing ? inventoryService.updateItemCategory(editing.id, { name: name.trim(), description: description.trim() }) : inventoryService.createItemCategory({ name: name.trim(), description: description.trim(), parent: null })),
    onSuccess: () => {
      reset();
      onChanged();
    },
  });
  const remove = useMutation({ mutationFn: inventoryService.deleteItemCategory, onSuccess: () => onChanged() });
  const sorted = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Modal isOpen size="2xl" title="Categories" onClose={onClose} footer={<button type="button" className="btn btn-outline" onClick={onClose}>Close</button>}>
      <div className="space-y-5">
        <p className="text-sm text-gray-600">Categories group stock and fixed assets, e.g. Stationery, Science lab, ICT. A category in use cannot be deleted.</p>
        {sorted.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {sorted.map((category) => (
              <li key={category.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">{category.name}</span>
                  <span className="ml-2 text-xs text-gray-500">{usage.get(category.id) ?? 0} in use</span>
                  {category.description && <span className="block text-gray-600">{category.description}</span>}
                </span>
                <span className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(category);
                      setName(category.name);
                      setDescription(category.description ?? "");
                    }}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                    aria-label={`Edit ${category.name}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {!usage.get(category.id) && (
                    <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(category.id)} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label={`Delete ${category.name}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500">No categories yet. Add the first below.</p>
        )}
        {remove.isError && errorLine(remove.error, "The category could not be deleted.")}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
          className="grid gap-3 rounded-lg border border-gray-200 p-4 sm:grid-cols-[1fr_1.5fr_auto] sm:items-end"
        >
          <Field label={editing ? `Rename ${editing.name}` : "New category"}>
            <input required value={name} onChange={(event) => setName(event.target.value)} maxLength={100} className="input mt-1" placeholder="e.g. Science lab" />
          </Field>
          <Field label="Description" optional>
            <input value={description} onChange={(event) => setDescription(event.target.value)} className="input mt-1" />
          </Field>
          <span className="flex gap-2">
            {editing && <button type="button" onClick={reset} className="btn btn-outline">Cancel</button>}
            <button disabled={save.isPending} className="btn btn-primary">{editing ? "Save" : "Add"}</button>
          </span>
          {save.isError && <div className="sm:col-span-3">{errorLine(save.error, "The category could not be saved.")}</div>}
        </form>
      </div>
    </Modal>
  );
}
